import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/apis/endpoints';
import { PROJECT_STATUS_COLORS } from '@/constants';
import type { ValueMap } from '@/apis/types';
import styles from './index.module.less';

/**
 * Projects Chart Panel - Shows project statistics pie chart
 */
export const ProjectsChartPanel: React.FC = () => {
  const { data: overallStat } = useQuery({
    queryKey: ['projectOverallStat'],
    queryFn: () => dashboardApi.getProjectOverallStat(),
  });

  const hasPositiveData = (data: ValueMap): boolean => {
    return Object.values(data).some((value) => typeof value === 'number' && value > 0);
  };

  const chartOptions = useMemo(
    () => ({
      tooltip: { show: false },
      legend: {
        orient: 'vertical',
        left: 150,
        top: 0,
      },
      series: [
        {
          name: 'Projects',
          type: 'pie',
          radius: [40, 60],
          center: [60, 60],
          avoidLabelOverlap: true,
          label: {
            show: false,
            position: 'center',
            formatter: `{b}\n{c}`,
            lineHeight: 20,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold',
            },
            scale: false,
          },
          labelLine: { show: false },
          data:
            overallStat && hasPositiveData(overallStat)
              ? [
                  {
                    value:
                      overallStat.total -
                      overallStat.errorCount -
                      overallStat.generatingCount -
                      overallStat.uploadingCount,
                    name: 'Active',
                    itemStyle: { color: PROJECT_STATUS_COLORS.ACTIVE },
                  },
                  {
                    value: overallStat.uploadingCount,
                    name: 'Uploading',
                    itemStyle: { color: PROJECT_STATUS_COLORS.UPLOADING },
                  },
                  {
                    value: overallStat.generatingCount,
                    name: 'Generating',
                    itemStyle: { color: PROJECT_STATUS_COLORS.GENERATING },
                  },
                  {
                    value: overallStat.errorCount,
                    name: 'Error',
                    itemStyle: { color: PROJECT_STATUS_COLORS.ERROR },
                  },
                ]
              : [],
        },
      ],
    }),
    [overallStat],
  );

  if (!overallStat || !hasPositiveData(overallStat)) {
    return null;
  }

  return (
    <div className={styles.panel} data-testid="dashboard-chart-container">
      <div className={styles.title}>Projects</div>
      <div className={styles.content}>
        <ReactECharts option={chartOptions} style={{ height: 130 }} data-testid="dashboard-chart-echart" />
      </div>
    </div>
  );
};

export default ProjectsChartPanel;
